import { IServerSideGetRowsRequest } from 'ag-grid-community';
import invert from 'lodash/invert';
import some from 'lodash/some';

import { composeQuery, prerenderedSelect, select, c } from 'common/soql_builder';
import { NamedSelect } from 'common/soql_builder/select';
import { GrandTotalRow } from 'common/types/agGrid/grandTotal';
import { ViewColumn } from 'common/types/viewColumn';

import { ColumnAggregation } from '../dataProviders/types';
import { getSoqlMetaBundle } from '../dataProviders/getSoqlMetaBundle';
import { getDisplayableColumns } from '../dataProviders/MetadataProvider';
import { Hierarchy, HierarchyColumnConfig, Vif } from '../vif';

import { constructAgRequestFromHierarchy } from './ConstructAgRequest';
import { isPivotInAgRequest, isPivotInHierarchy } from './PivotHelpers';
import { whereClauseFilteringOwnColumn } from './SoqlHelpers';
import { findCorrespondingColumn } from './TableDataColumnHelpers';
import {
  createMeasureAliasMap,
  createSelectAliasMap,
  getSelectAggregationsQuery,
  unaliasRowData,
} from './TableDataQueryHelpers';
import { isTableAdvancedTotalingEnabled } from './VifSelectors';
import { generateCascadingPipedQuery, getAdvancedTotalingAggregations } from './TableDataFormulaHelpers';

export function grandTotalQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
  nonStandardAggregations?: ColumnAggregation[] | null,
  hierarchyConfigs?: HierarchyColumnConfig[],
) {
  // The grand total query is the same as the table data query except that we don't want to group,
  // unless we are pivoting. We also include the where clause to handle filtering on the grand total
  const { valueCols, pivotCols } = agRequest;

  if (isTableAdvancedTotalingEnabled()) {
    // A Grand Total request conceptually corresponds to being at level -1
    const currentQueryGroupLevel = -1;
    const pipedQuery = generateCascadingPipedQuery({
      columns,
      legacyWhereClause,
      agRequest,
      currentQueryGroupLevel,
      hierarchyConfigs,
      nonStandardAggregations,
    });

    if (pipedQuery) {
      return pipedQuery;
    }
  }

  let selectParts: NamedSelect[] = [];

  if (isTableAdvancedTotalingEnabled() && hierarchyConfigs) {
    // This ensures we start applying formulas from the top-most configuration.
    selectParts = getAdvancedTotalingAggregations(
      valueCols,
      columns,
      hierarchyConfigs,
      0,
      nonStandardAggregations,
    );
  }

  if (selectParts.length === 0) {
    selectParts = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
  }

  // But when we are pivoting, we need to GROUP BY the pivot column
  const groupParts: ViewColumn[] = [];
  if (isPivotInAgRequest(agRequest)) {
    const selectAliasMap: { [key: string]: string } = createSelectAliasMap(agRequest, columns);
    pivotCols.forEach((pivotCol) => {
      const pivotColId = findCorrespondingColumn(columns, pivotCol);
      if (typeof pivotColId !== 'undefined') {
        selectParts.push(select(pivotColId, selectAliasMap[pivotCol.id]));
        groupParts.push(pivotColId);
      }
    });
  }

  const dataQuery = composeQuery({
    selects: selectParts,
    groups: groupParts,
    legacyWhereClause,
  });
  return dataQuery;
}

/**
 * Utilizes the Soql Query Builder or SoqlDataProvider depending on if there are hierarchies, ie: Ag Grid tables.
 * @param {Object} vifForDataQuery - Vif of the given table.

 */
export async function setGrandTotalDataQuery(
  vifForDataQuery: Vif,
  hierarchyConfig: Hierarchy,
  shouldFetchAggregationSuggestions: boolean,
) {
  const showGrandTotal = hierarchyConfig.showGrandTotal;
  const hasAggregationColumn = some(
    hierarchyConfig.columnConfigurations,
    (h) => h.aggregation !== null && !h.hidden,
  );

  if (!showGrandTotal || !hasAggregationColumn) {
    return [];
  }

  const { soqlDataProvider, metadataProvider } = getSoqlMetaBundle(vifForDataQuery);
  const datasetMetadata = await metadataProvider.getDatasetMetadata();
  const displayableColumns = getDisplayableColumns(datasetMetadata);
  const whereClause = whereClauseFilteringOwnColumn(vifForDataQuery, 0);
  const nonStandardAggregations = shouldFetchAggregationSuggestions
    ? await metadataProvider.getAggregationSuggestions()
    : null;

  const fakeAgRequest = constructAgRequestFromHierarchy(hierarchyConfig);

  const aliasedGrandTotalRow: { [measureAlias: string]: string }[] = await soqlDataProvider.invokeSoqlQuery(
    grandTotalQuery,
    [
      displayableColumns,
      whereClause,
      fakeAgRequest,
      nonStandardAggregations,
      hierarchyConfig.columnConfigurations,
    ],
  );

  const aggAliases = createMeasureAliasMap(fakeAgRequest);
  let selectAliases: { [key: string]: string } = {};
  if (isPivotInHierarchy(hierarchyConfig)) {
    const selectAliasMap: { [key: string]: string } = createSelectAliasMap(fakeAgRequest, displayableColumns);
    selectAliases = invert(selectAliasMap);
  }

  const finalGrandTotalRow: GrandTotalRow = unaliasRowData(aliasedGrandTotalRow, {
    ...aggAliases,
    ...selectAliases,
  });

  return finalGrandTotalRow;
}
